import { useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ImageGeneratedResult } from "../../types/activity-message-types"

import { AlertCircle, Download, ImageIcon, Pencil, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { isImageFinishedFrame, sessionFileContentUrl } from "@/features/chat/utils/image-activities"
import {
  imageExtensionFromDataUrl,
  imageFileExtension,
  imageMimeTypeForFilename,
} from "@/features/chat/utils/image-file-mime"
import { useTurnScopedFileContent } from "@/hooks/use-turn-scoped-file-content"
import { fileToDataUrl } from "@/lib/file-utils"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { ImageCacheService } from "@/services/image-cache-service"

type ImageResultProps = {
  activity: ImageGeneratedResult
  onEditRequest?: (editPrompt: string, imageUrl: string) => void
  onOpenInEditor?: (imageUrl: string) => void
  demoMode?: boolean
  /** Chat session id; with `activity.session_file` it recovers a stored image (#3043). */
  sessionId?: string
  /**
   * The turn that produced this activity is still running. A stored image that
   * cannot be fetched yet is then shown as "Loading…" rather than as an error:
   * the fetch uses the streaming retry policy (a few retries, re-attempted on
   * remount) and gets one fresh attempt once the turn has ended (#3442).
   */
  isStreaming?: boolean
  /**
   * Id of the turn (message id, or a stable slot key while unknown). Scopes the
   * finished-turn lookup of a stored image so a miss cached while streaming
   * gets one fresh attempt once the turn's files are registered.
   */
  turnId?: string
  /** The slot key the turn used before `turnId` became its message id; see useTurnScopedFileContent. */
  slotTurnId?: string
}

type ImageLoadState = {
  src: string
  status: "loaded" | "error"
}

// Object URLs for stored images, one per buffer and MIME type, shared by every
// mount of the same bytes: the page remounts the card when the message id
// arrives, and a URL the browser has already decoded shows at once instead of
// behind the placeholder. A URL is retained while a card shows it and for a
// short grace period after the last one leaves (the remount happens within
// it), then revoked, so no copy of the bytes outlives the view for long.
type SharedObjectUrl = { url: string; refs: number; release: ReturnType<typeof setTimeout> | null }
const OBJECT_URL_GRACE_MS = 10_000
const sharedObjectUrls = new WeakMap<ArrayBuffer, Map<string, SharedObjectUrl>>()
const decodedObjectUrls = new Set<string>()

function scheduleRelease(byMimeType: Map<string, SharedObjectUrl>, mimeType: string): void {
  const entry = byMimeType.get(mimeType)
  if (!entry) return
  if (entry.release) clearTimeout(entry.release)
  entry.release = setTimeout(() => {
    if (entry.refs > 0 || byMimeType.get(mimeType) !== entry) return
    byMimeType.delete(mimeType)
    decodedObjectUrls.delete(entry.url)
    URL.revokeObjectURL(entry.url)
  }, OBJECT_URL_GRACE_MS)
}

function sharedObjectUrl(buffer: ArrayBuffer, mimeType: string): SharedObjectUrl {
  let byMimeType = sharedObjectUrls.get(buffer)
  if (!byMimeType) {
    byMimeType = new Map()
    sharedObjectUrls.set(buffer, byMimeType)
  }
  let entry = byMimeType.get(mimeType)
  if (!entry) {
    entry = {
      url: URL.createObjectURL(new Blob([buffer], { type: mimeType })),
      refs: 0,
      release: null,
    }
    byMimeType.set(mimeType, entry)
    // Created during render: released on the same schedule if no mount retains it.
    scheduleRelease(byMimeType, mimeType)
  }
  return entry
}

function retainObjectUrl(entry: SharedObjectUrl): void {
  entry.refs += 1
  if (entry.release) {
    clearTimeout(entry.release)
    entry.release = null
  }
}

function releaseObjectUrl(buffer: ArrayBuffer, mimeType: string, entry: SharedObjectUrl): void {
  entry.refs = Math.max(0, entry.refs - 1)
  if (entry.refs === 0) {
    const byMimeType = sharedObjectUrls.get(buffer)
    if (byMimeType) scheduleRelease(byMimeType, mimeType)
  }
}

export function ImageResult({
  activity,
  onEditRequest,
  onOpenInEditor,
  demoMode = false,
  sessionId,
  isStreaming,
  turnId,
  slotTurnId,
}: ImageResultProps) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [editPrompt, setEditPrompt] = useState("")
  const [isImageExpanded, setIsImageExpanded] = useState(false)

  // A source the <img> can show directly: the live frame, or the in-memory
  // cache on the same page. Both are data URLs and cannot fail transiently.
  const directImageUrl = useMemo(() => {
    if (activity.image_url) return activity.image_url
    return (activity.job_id && ImageCacheService.getImage(activity.job_id)) || ""
  }, [activity.image_url, activity.job_id])

  // Otherwise the file the tool stored in the session: a reloaded page, or a
  // durable turn, whose stored activity has `image_url` stripped (#3043). It is
  // fetched with credentials through `useFileContent`; a plain <img src> would
  // carry none in the dev setup and 401 (#3442).
  const sessionFile =
    !directImageUrl && sessionId && activity.session_file ? activity.session_file : ""
  const sessionFileUrl =
    sessionFile && sessionId ? sessionFileContentUrl(sessionId, sessionFile) : undefined

  // Shared turn-scoped policy with the inline answer image: retries while the
  // turn streams, one fresh attempt once it has finished, bytes reused only
  // from this turn's own cache entries (see useTurnScopedFileContent).
  const { data: fileBuffer, isError: fileUnavailable } = useTurnScopedFileContent(
    sessionFileUrl,
    !!sessionFileUrl,
    { isStreaming, turnId, slotTurnId }
  )

  const sessionFileMimeType = sessionFile ? imageMimeTypeForFilename(sessionFile) : ""
  const sharedUrl = useMemo(
    () => (fileBuffer && sessionFile ? sharedObjectUrl(fileBuffer, sessionFileMimeType) : null),
    [fileBuffer, sessionFile, sessionFileMimeType]
  )
  useEffect(() => {
    if (!fileBuffer || !sharedUrl) return
    retainObjectUrl(sharedUrl)
    return () => releaseObjectUrl(fileBuffer, sessionFileMimeType, sharedUrl)
  }, [fileBuffer, sessionFileMimeType, sharedUrl])
  const objectUrl = sharedUrl?.url ?? null

  const imageSrc = directImageUrl || objectUrl || ""
  const hasSource = Boolean(directImageUrl || sessionFileUrl)
  // Mid-turn, a stored file that cannot be fetched yet is not an error: the
  // hook keeps retrying and the turn's end brings a fresh attempt.
  const sessionFileError = Boolean(sessionFileUrl) && !objectUrl && fileUnavailable && !isStreaming
  // Studio AI's editor only loads data: or blob: URLs (its <img> cannot send
  // auth headers), and this card's object URL lives only as long as the
  // fetched bytes are cached, so a stored image is handed over as a data URL
  // built from those bytes.
  // Rejects when the bytes are not loaded or cannot be read; callers keep the
  // user's input and say so.
  const resolveEditorImageUrl = async (): Promise<string> => {
    if (directImageUrl) return directImageUrl
    if (!fileBuffer || !sessionFile) throw new Error("image bytes are not loaded")
    return fileToDataUrl(
      new File([fileBuffer], sessionFile, { type: imageMimeTypeForFilename(sessionFile) })
    )
  }
  // Read failures are reported to the user; a failure inside the callback
  // itself is a bug in the receiver and is logged, not blamed on the read.
  // One hand-off at a time: reading a stored image takes a moment, and a
  // second Enter or click meanwhile would start a second billed edit. The ref
  // blocks synchronously; the state marks the controls aria-disabled (not
  // disabled, which would drop keyboard focus from the focused button and
  // hide the focus-revealed overlay).
  const handOffInFlight = useRef(false)
  const [isHandingOff, setIsHandingOff] = useState(false)
  const handOff = (receive: (imageUrl: string) => void): Promise<void> => {
    if (handOffInFlight.current) return Promise.resolve()
    handOffInFlight.current = true
    setIsHandingOff(true)
    return resolveEditorImageUrl()
      .then(
        (imageUrl) => {
          try {
            receive(imageUrl)
          } catch (error) {
            logger.error(
              "Image handoff callback failed",
              error instanceof Error ? error : { error }
            )
          }
        },
        (error: unknown) => {
          logger.warn(
            "Could not read the generated image for handoff",
            error instanceof Error ? error : { error }
          )
          toast.error(t("image.handoff_error", "Could not read the image. Try again."))
        }
      )
      .finally(() => {
        handOffInFlight.current = false
        setIsHandingOff(false)
      })
  }

  // Outcome of the <img> load, keyed by the src it belongs to, so a new source
  // starts clean. The element shows the last image the browser loaded until a
  // newer load succeeds (replacing it) or fails (clearing it); mirror exactly
  // that, so a frame swap keeps the previous frame on screen while the next
  // decodes and a failure drops to the placeholder.
  const [loadState, setLoadState] = useState<ImageLoadState | null>(null)
  // A shared object URL this page has decoded before (a remount) needs no
  // new load event to count as shown.
  const knownDecoded = decodedObjectUrls.has(imageSrc)
  const isImageLoaded =
    (loadState?.src === imageSrc && loadState.status === "loaded") ||
    (knownDecoded && loadState?.src !== imageSrc)
  const loadFailed = loadState?.src === imageSrc && loadState.status === "error"
  const showImage =
    Boolean(imageSrc) &&
    !loadFailed &&
    ((loadState !== null && loadState.status === "loaded") || knownDecoded)
  const imageError = loadFailed || sessionFileError

  // The stored filename knows the format on every path (the live frame's data
  // URL is also what got stored); a frame that was never stored falls back to
  // its data URL header, then to PNG, the tool's default.
  const downloadExtension =
    (activity.session_file && imageFileExtension(activity.session_file)) ||
    imageExtensionFromDataUrl(directImageUrl) ||
    "png"

  const handleDownload = () => {
    const link = document.createElement("a")
    link.href = imageSrc
    link.download = `generated-image-${Date.now()}.${downloadExtension}`
    try {
      document.body.appendChild(link)
      link.click()
    } finally {
      try {
        if (link.parentNode === document.body) {
          document.body.removeChild(link)
        }
      } catch {
        // Element may have been removed already, ignore
      }
    }
  }

  const handleEditSubmit = () => {
    const prompt = editPrompt.trim()
    if (!prompt || !onEditRequest) return
    void handOff((imageUrl) => {
      onEditRequest(prompt, imageUrl)
      setEditPrompt("")
      setIsEditing(false)
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleEditSubmit()
    } else if (e.key === "Escape") {
      setIsEditing(false)
      setEditPrompt("")
    }
  }

  const displayPrompt = activity.revised_prompt || activity.prompt
  const isPartial = Boolean(activity.partial)
  // Pixels done (the final frame, or a 100 % partial while the backend stores
  // the file): the one rule shared with dedupe and the image cache.
  const pixelsDone = isImageFinishedFrame(activity)

  // `model_label` ships with the `image_generated` activity details once the
  // image-model registry has resolved a model (#3038).
  const modelLabel = activity.model_label
  const modelCaption = modelLabel ? (
    <p className="text-muted-foreground/60 text-[11px]">
      {t("image.generatedWith", "Generated with {{model}}", { model: modelLabel })}
    </p>
  ) : null
  const progressPct =
    typeof activity.progress === "number" ? Math.max(0, Math.min(100, activity.progress)) : null
  // A partial at 100% means the pixels are done and the file is being stored
  // (up to IMAGE_PERSIST_TIMEOUT_S on the backend): say so rather than
  // "Generating… 100%", which reads as a hang.
  let progressLabel = t("image.generating", "Generating…")
  if (isPartial && pixelsDone) {
    progressLabel = t("image.saving", "Saving…")
  } else if (progressPct !== null) {
    progressLabel = t("image.generating_with_progress", "Generating… {{progress}}%", {
      progress: progressPct,
    })
  }

  // No source at all. If a stream is in-flight (partial=true) show a progress
  // placeholder that mirrors the final layout — same border, same max-width — so
  // the UI doesn't pop/reflow when the first partial frame arrives. Otherwise
  // fall back to the historic "not available" message.
  if (!hasSource) {
    if (isPartial) {
      return (
        <section aria-label="Generated image" className="flex flex-col gap-3">
          <div className="border-border/50 bg-muted/20 relative h-64 w-full max-w-md overflow-hidden rounded-lg border">
            <div className="text-muted-foreground flex h-full w-full flex-col items-center justify-center gap-2">
              <ImageIcon className="size-8 animate-pulse" />
              <span className="text-sm">{progressLabel}</span>
            </div>
            <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-1 bg-black/20">
              <div
                className="h-full bg-white/90 transition-[width] duration-500 ease-out"
                style={{ width: `${progressPct ?? 0}%` }}
              />
            </div>
          </div>
          {displayPrompt && (
            <p className="text-muted-foreground/70 line-clamp-2 text-xs italic">{displayPrompt}</p>
          )}
          {modelCaption}
        </section>
      )
    }
    return (
      <section aria-label="Generated image" className="flex flex-col gap-3">
        <div className="border-border/50 bg-muted/20 flex h-32 w-full max-w-md items-center justify-center rounded-lg border">
          <div className="text-muted-foreground flex flex-col items-center gap-2">
            <ImageIcon className="size-8" />
            <span className="text-sm">
              {t("image.not_available", "Image generated but not available in history")}
            </span>
          </div>
        </div>
        {displayPrompt && (
          <p className="text-muted-foreground/70 line-clamp-2 text-xs italic">{displayPrompt}</p>
        )}
      </section>
    )
  }

  return (
    <section aria-label="Generated image" className="flex flex-col gap-3">
      {/* Image container */}
      <div className="border-border/50 bg-muted/20 relative inline-block max-w-md overflow-hidden rounded-lg border">
        <button
          className="focus:ring-ring block cursor-zoom-in focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-default"
          disabled={imageError || !showImage}
          type="button"
          onClick={() => !imageError && showImage && setIsImageExpanded(true)}
        >
          {imageSrc && (
            <img
              alt={displayPrompt}
              src={imageSrc}
              className={cn(
                "block max-w-full rounded-lg transition-opacity duration-300",
                showImage ? "opacity-100" : "opacity-0"
              )}
              onError={() => {
                decodedObjectUrls.delete(imageSrc)
                setLoadState({ src: imageSrc, status: "error" })
              }}
              onLoad={() => {
                if (imageSrc.startsWith("blob:")) decodedObjectUrls.add(imageSrc)
                setLoadState({ src: imageSrc, status: "loaded" })
              }}
            />
          )}
          {!showImage && !imageError && (
            <div className="flex h-64 w-64 max-w-full items-center justify-center">
              <div className="text-muted-foreground flex flex-col items-center gap-2">
                <ImageIcon className="size-8 animate-pulse" />
                <span className="text-sm">{t("common.loading", "Loading...")}</span>
              </div>
            </div>
          )}
          {imageError && (
            <div className="flex h-32 w-64 max-w-full items-center justify-center">
              <div className="text-destructive flex flex-col items-center gap-2">
                <AlertCircle className="size-8" />
                <span className="text-sm">{t("image.load_error", "Failed to load image")}</span>
              </div>
            </div>
          )}
        </button>

        {/* Progress overlay while streaming partials. Sits on top of the image so the
            user sees the percentage refine as 50% → 75% → final. The bottom progress bar
            keeps animating so the wait doesn't feel stalled even between partial frames. */}
        {isPartial && showImage && (
          <>
            <div className="pointer-events-none absolute top-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
              {progressLabel}
            </div>
            <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-1 bg-black/30">
              <div
                className="h-full bg-white/90 transition-[width] duration-500 ease-out"
                style={{ width: `${progressPct ?? 0}%` }}
              />
            </div>
          </>
        )}

        {/* Action buttons overlay. On a live turn the 100 % frame already holds
            the pixels as a data URL, so download and the hand-offs work before
            the stored file is named; only a frame still fetching waits. */}
        {isImageLoaded && !demoMode && (!isPartial || (pixelsDone && Boolean(directImageUrl))) && (
          <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 hover:opacity-100 [.relative:hover_&]:opacity-100">
            <Button
              aria-label={t("image.download", "Download image")}
              className="bg-background/80 hover:bg-background size-8 backdrop-blur-sm"
              size="icon"
              variant="outline"
              onClick={handleDownload}
            >
              <Download className="size-4" />
            </Button>
            {onOpenInEditor && (
              <Button
                aria-disabled={isHandingOff}
                aria-label={t("image.openInEditor", "Edit in Studio AI")}
                className="bg-background/80 hover:bg-background size-8 backdrop-blur-sm aria-disabled:opacity-50"
                size="icon"
                variant="outline"
                onClick={() => {
                  void handOff(onOpenInEditor)
                }}
              >
                <Pencil className="size-4" />
              </Button>
            )}
            {onEditRequest && (
              <Button
                aria-label={t("image.edit", "Edit image")}
                className="bg-background/80 hover:bg-background size-8 backdrop-blur-sm"
                size="icon"
                variant="outline"
                onClick={() => setIsEditing(true)}
              >
                <Pencil className="size-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Prompt caption */}
      {displayPrompt && (
        <p className="text-muted-foreground/70 line-clamp-2 text-xs italic">{displayPrompt}</p>
      )}

      {/* Which model produced it — only when the backend reports one */}
      {modelCaption}

      {/* Edit input */}
      {isEditing && (
        <div className="flex gap-2">
          <Input
            className="flex-1 text-sm"
            placeholder={t("image.edit_placeholder", "Describe the changes you want to make...")}
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button
            aria-disabled={isHandingOff}
            className="aria-disabled:opacity-50"
            disabled={!editPrompt.trim()}
            size="sm"
            onClick={handleEditSubmit}
          >
            {t("common.apply", "Apply")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setIsEditing(false)
              setEditPrompt("")
            }}
          >
            <X className="size-4" />
          </Button>
        </div>
      )}

      {/* Expanded image modal */}
      <Dialog open={isImageExpanded} onOpenChange={setIsImageExpanded}>
        <DialogContent className="flex w-auto max-w-[90vw] items-center justify-center border-none bg-transparent p-0 text-white shadow-none">
          <DialogTitle className="sr-only">
            {displayPrompt || t("image.generated_image", "Generated image")}
          </DialogTitle>
          {imageSrc && (
            <img
              alt={displayPrompt}
              className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
              src={imageSrc}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  )
}
